import { Topic, TopicCategory } from "shared/types";
import TopicDetailSeo from "../TopicDetailSeo";
import { getFormatTopicDate } from "features/media/utils/getFormatTopicDate";
import tw from "twin.macro";
import { TwitterShareButton } from "react-share";
import { TwitterIcon } from "public/media/icons";
import { useTopicMetaData } from "features/media/hooks/useTopicMetaData";
import { Image } from "shared/components/atoms/Image";
import Link from "next/link";
import { Paths } from "utils/routes";
import { AdUnitProps } from "features/media/utils/AdUnitProps";
import { AdSlot as AdSlotBase } from "react-dfp";
import { TopicElement as TopicElementType } from "shared/types";
import Script from "next/script";
import { TopicElement } from "../molecules/TopicElement";
import { ColumnList } from "features/media/components/molecules/ColumnList";
import { Button } from "shared/components/atoms/Button";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { useTopicCategoryList } from "repositories/hooks/useTopicCategoryList";
import { MultiColumnList } from "../molecules/MultiColumnList";
import { CategoryAndTagList } from "components/organisms/CategoryAndTagList";
import dayjs from "dayjs";
import { useCookies } from "react-cookie";
import { FreeFortuneContentList } from "features/Home/components/FreeFortuneContentList";

const Date = tw.time`font-light text-2xs text-secondary tracking-[.25px] leading-[19px]`;

const AdContainer = tw.div`flex-center mb-8`;

const AdSlot = tw(AdSlotBase)`w-[90vw] flex-center`;

const ColumnListContainer = tw.div`w-full mb-8`;

const H2 = tw.h2`font-base text-lg leading-none tracking-[-0.17px] p-4`;

const MoreButton = tw(
  Button
)`rounded w-11/12 h-12 mx-auto my-4 font-normal text-sm leading-none tracking-[.41px]`;

const breadcrumbCss = tw`font-light text-2xs tracking-[.25px] leading-[19px] text-[#cf73b2]`;

interface Props {
  topic: Topic;
  relatedTopics?: Topic[];
  pressTopics?: Topic[];
  miracleTopics?: Topic[];
}

export const TopicDetail = ({
  topic,
  relatedTopics,
  pressTopics,
  miracleTopics,
}: Props) => {
  const { categories } = useTopicCategoryList(topic.mediaType);

  const { push } = useRouter();
  const popinScriptRef = useRef<HTMLDivElement>();
  const [, setCookie] = useCookies();

  useEffect(() => {
    setCookie("topic_id", topic.id, { path: "/" });
    setCookie("date", dayjs(), { path: "/" });
  }, [topic.id, setCookie]);

  const { publishedAt, lastmod } = getFormatTopicDate(topic);
  const { url, thumbnailUrl } = useTopicMetaData(topic);

  const adUnitProps = AdUnitProps.adTypes[topic.adType!];

  const handleMoreRelatedTopics = async () => {
    await push(`/${topic.mediaType}/column-category/${topic.category.alias}`);
  };

  const handleMoreFreeFortuneContents = async () => {
    await push(Paths.freeFortuneContents.index());
  };

  const handleMorePressTopics = async () => {
    await push(`/press`);
  };

  const handleMoreMiracleTopics = async () => {
    await push(`/miracle`);
  };

  const handleClickCategory = async (data: TopicCategory) => {
    await push(`/${data.mediaType}/column-category/${data.alias}`);
  };

  const handleLoadPopinScript = async () => {
    const script = document.getElementsByTagName("script")[0];
    script.parentNode.insertBefore(popinScriptRef.current, script);
  };

  return (
    <>
      <TopicDetailSeo topic={topic} />
      <Script src="https://platform.twitter.com/widgets.js" async />
      {process.env.NEXT_PUBLIC_APP_ENV === "production" && (
        <Script src="//widgets.outbrain.com/outbrain.js" />
      )}
      <div ref={popinScriptRef}>
        <Script
          src="//api.popin.cc/searchbox/miror.js"
          type="text/javascript"
          async
          onLoad={handleLoadPopinScript}
        />
      </div>
      <article tw="p-4" css={{ maxWidth: "inherit" }}>
        <h1 tw="font-base text-lg tracking-[.53px]">{topic.title}</h1>
        <div tw="flex items-center justify-between my-4">
          <div>
            <Date tw="mr-3">公開日: {publishedAt}</Date>
            <Date>更新日: {lastmod}</Date>
          </div>
          <TwitterShareButton
            tw="bg-twitter rounded w-[83px] h-6 text-white border-none p-0 cursor-pointer flex-center"
            url={url}
            title={topic.title}
            resetButtonStyle={false}
          >
            <TwitterIcon tw="w-[14px] mt-[1px] mr-1.5" />
            Tweet
          </TwitterShareButton>
        </div>
        {topic.thumbnail && (
          <Image
            tw="mb-8"
            src={thumbnailUrl}
            layout="dynamic"
            priority
            alt={topic.title}
          />
        )}
        <p tw="font-light text-xs tracking-[.35px] leading-6 text-black mb-8">
          {topic.meta}
        </p>
        <ol tw="inline-block list-none mb-8">
          <li tw="mr-1 inline">
            <Link href={Paths.home()} passHref>
              <a css={breadcrumbCss}>チャット占い・電話占い</a>
            </Link>
            <span css={breadcrumbCss}>{" > "}</span>
            <Link href={Paths.tags.show(topic.tag.id)} passHref>
              <a css={breadcrumbCss}>{topic.tag.name}</a>
            </Link>
            <span css={breadcrumbCss}>{" > "}</span>
            <span css={breadcrumbCss}>{topic.title}</span>
          </li>
        </ol>
        {adUnitProps && (
          <AdContainer>
            <AdSlot {...adUnitProps.pageTop(topic)} />
          </AdContainer>
        )}
        {topic.topicElements.map((e: TopicElementType, index: number) => (
          <TopicElement key={e.id} data={e} index={index} topic={topic} />
        ))}
        {adUnitProps && !topic?.isContributeToConversionToMiror && (
          <AdContainer>
            <AdSlot {...AdUnitProps.forAds.pageBottom(topic)} />
          </AdContainer>
        )}
        <div tw="bg-[#fff1fb] py-4 px-6 rounded-md border border-solid border-[#cf73b2] mb-8">
          <p tw="font-normal text-2xs tracking-[.5px] leading-4 text-[#cf80bc]">
            記事の内容は、法的正確性を保証するものではありません。サイトの情報を利用し判断または行動する場合は、弁護士にご相談の上、ご自身の責任で行ってください。
          </p>
        </div>
      </article>
      {adUnitProps && (
        <AdContainer>
          <AdSlot {...adUnitProps.pageBottom(topic)} />
        </AdContainer>
      )}
      {relatedTopics && (
        <ColumnListContainer>
          <H2>関連コラム</H2>
          <ColumnList topics={relatedTopics} />
          <MoreButton variant="outline" onClick={handleMoreRelatedTopics}>
            {`${topic.category.name}のコラムをもっと見る`}
          </MoreButton>
        </ColumnListContainer>
      )}
      {adUnitProps && (
        <AdContainer>
          <AdSlot {...adUnitProps.pageBottomSecondary(topic)} />
        </AdContainer>
      )}
      <div
        className={`OUTBRAIN`}
        data-src="DROP_PERMALINK_HERE"
        data-widget-id="MB_1"
      />
      <div id="_popIn_recommend" />
      <ColumnListContainer>
        <H2>新着無料占い</H2>
        <FreeFortuneContentList tw="px-5" />
        <MoreButton variant="outline" onClick={handleMoreFreeFortuneContents}>
          新着無料占いをもっと見る
        </MoreButton>
      </ColumnListContainer>
      {pressTopics && (
        <ColumnListContainer>
          <H2>新着恋愛コラム</H2>
          <ColumnList topics={pressTopics} />
          <MoreButton variant="outline" onClick={handleMorePressTopics}>
            恋愛コラムをもっと見る
          </MoreButton>
        </ColumnListContainer>
      )}
      {miracleTopics && (
        <ColumnListContainer>
          <H2>新着占いコラム</H2>
          <ColumnList topics={miracleTopics} />
          <MoreButton variant="outline" onClick={handleMoreMiracleTopics}>
            占いコラムをもっと見る
          </MoreButton>
        </ColumnListContainer>
      )}
      {categories && (
        <MultiColumnList
          title="コラムカテゴリ"
          data={categories}
          keyName="name"
          onClick={handleClickCategory}
        />
      )}
      <CategoryAndTagList tw="mt-4" />
    </>
  );
};
